/**
 * Suspicious v2 §3.4 — single BlockControl component used everywhere.
 *
 * Split-button `Block for 7 days ▾` + inline confirms:
 *   - Forever → amber "Block {ip} forever?" confirm
 *   - Shared network + forever → "{ip} is on a home or mobile network..."
 *   - Self → "This is your current address..."
 *   - Trust → "Trust {ip}?"
 * Status line below replaces the old SUGGESTED ACTION kicker / 5-button
 * row / enforce button. Copy strictly follows spec §3.4.
 *
 * Watch-only strings forbidden: "Observe", "Enforce", "Watch only",
 * "Firewall mode", "Suggested action", "Verdict". The dashboard test
 * `suspicious-no-banned-strings.test.ts` grep-asserts their absence.
 */

import { useState } from 'react';
import type { ReactNode } from 'react';
import { ChevronDown, ShieldAlert, ShieldCheck, ShieldOff } from 'lucide-react';

import type { BlockingState, ManualBlockDuration } from '@sill/types/api';
import { Tooltip } from '../../components/atoms/Tooltip.js';
import { useOptionalSites } from '../../app/providers.js';
import { useC } from '../../lib/theme.js';
import { SHOPIFY_SAFE_WORKER_AVAILABLE } from '../onboarding/install-copy.js';

const monoFont = "'JetBrains Mono', monospace";
const sansFont = "'Geist', sans-serif";

export type BlockControlDuration = ManualBlockDuration;

export type BlockControlOutcome = {
  readonly action: 'block';
  readonly duration: BlockControlDuration;
  readonly confirm_self?: true;
  readonly confirm_shared?: true;
} | {
  readonly action: 'trust';
} | {
  readonly action: 'ignore';
};

export interface BlockControlProps {
  /** Address shown in confirm copy (e.g. "203.0.113.7" or "{ip}/64"). */
  readonly targetDisplay: string;
  /** Domain shown in self-block confirm (what the merchant locks themselves out of). */
  readonly siteDomain: string | null;
  /** Does the control include the Ignore secondary (threat surfaces only). */
  readonly showIgnore?: boolean;
  /**
   * Default duration label the split button shows. The split-button
   * updates to the user's choice from the menu.
   */
  readonly defaultDuration?: BlockControlDuration;
  /**
   * Whether to render the shared-network confirm when the merchant
   * picks "forever" (the caller passes `true` when the sender's
   * network type is residential/mobile).
   */
  readonly sharedNetwork?: boolean;
  /** Whether the target overlaps the caller's IP (self-block guard). */
  readonly isSelf?: boolean;
  /** State chip + any error from the last attempt. */
  readonly pending?: boolean;
  readonly error?: string | null;
  /** Callback for the final action. */
  readonly onAction: (outcome: BlockControlOutcome) => void | Promise<void>;
}

const DURATION_LABELS: Record<BlockControlDuration, string> = {
  '24h': 'Block for 24 hours',
  '7d': 'Block for 7 days',
  forever: 'Block forever',
};
const DURATION_MENU: Record<BlockControlDuration, string> = {
  '24h': '24 hours',
  '7d': '7 days (recommended)',
  forever: 'Forever',
};

type ConfirmShape =
  | { readonly kind: 'forever' }
  | { readonly kind: 'shared' }
  | { readonly kind: 'self' }
  | { readonly kind: 'trust' };

export function BlockControl(props: BlockControlProps): JSX.Element {
  const C = useC();
  const [duration, setDuration] = useState<BlockControlDuration>(props.defaultDuration ?? '7d');
  const [open, setOpen] = useState(false);
  const [confirm, setConfirm] = useState<ConfirmShape | null>(null);

  const closeMenu = (): void => { setOpen(false); };

  const chooseDuration = (d: BlockControlDuration): void => {
    setDuration(d);
    setOpen(false);
  };

  const triggerBlock = (): void => {
    // Order of priority: self > shared > plain. Only forever triggers
    // the shared-network confirm; 24h / 7d skip it.
    if (props.isSelf === true) {
      setConfirm({ kind: 'self' });
      return;
    }
    if (duration === 'forever' && props.sharedNetwork === true) {
      setConfirm({ kind: 'shared' });
      return;
    }
    if (duration === 'forever') {
      setConfirm({ kind: 'forever' });
      return;
    }
    void props.onAction({ action: 'block', duration });
  };

  const triggerTrust = (): void => { setConfirm({ kind: 'trust' }); };
  const triggerIgnore = (): void => { void props.onAction({ action: 'ignore' }); };

  const confirmThen = async (c: ConfirmShape, pick24h?: boolean): Promise<void> => {
    setConfirm(null);
    if (c.kind === 'trust') {
      await props.onAction({ action: 'trust' });
      return;
    }
    if (c.kind === 'self') {
      await props.onAction({ action: 'block', duration, confirm_self: true });
      return;
    }
    if (c.kind === 'shared') {
      if (pick24h === true) {
        setDuration('24h');
        await props.onAction({ action: 'block', duration: '24h' });
      } else {
        await props.onAction({ action: 'block', duration: 'forever', confirm_shared: true });
      }
      return;
    }
    // 'forever'
    await props.onAction({ action: 'block', duration: 'forever' });
  };

  return (
    <div data-testid="block-control" style={{ fontFamily: sansFont, color: C.text }}>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, alignItems: 'center' }}>
        <div style={{ display: 'inline-flex', position: 'relative' }}>
          <button
            type="button"
            onClick={triggerBlock}
            disabled={props.pending === true}
            data-testid="block-control-primary"
            style={{
              fontFamily: sansFont,
              fontSize: 13,
              background: C.lime,
              color: C.ink,
              border: `1px solid ${C.lime}`,
              borderRight: `1px solid ${C.ink}66`,
              borderRadius: '2px 0 0 2px',
              padding: '7px 12px',
              cursor: props.pending === true ? 'wait' : 'pointer',
              opacity: props.pending === true ? 0.6 : 1,
            }}
          >
            {DURATION_LABELS[duration]}
          </button>
          <button
            type="button"
            onClick={() => setOpen((o) => !o)}
            disabled={props.pending === true}
            aria-haspopup="menu"
            aria-expanded={open}
            data-testid="block-control-menu-toggle"
            style={{
              fontFamily: sansFont,
              background: C.lime,
              color: C.ink,
              border: `1px solid ${C.lime}`,
              borderRadius: '0 2px 2px 0',
              padding: '7px 8px',
              cursor: props.pending === true ? 'wait' : 'pointer',
              display: 'inline-flex', alignItems: 'center',
              opacity: props.pending === true ? 0.6 : 1,
            }}
          >
            <ChevronDown size={14} />
          </button>
          {open ? (
            <div
              role="menu"
              data-testid="block-control-menu"
              onMouseLeave={closeMenu}
              style={{
                position: 'absolute', top: '100%', left: 0, marginTop: 4,
                minWidth: 200,
                background: C.surface,
                border: `1px solid ${C.border}`,
                borderRadius: 2,
                zIndex: 10,
              }}
            >
              {(['24h','7d','forever'] as const).map((d) => (
                <button
                  key={d}
                  type="button"
                  role="menuitem"
                  onClick={() => chooseDuration(d)}
                  data-duration={d}
                  style={{
                    display: 'block',
                    width: '100%',
                    textAlign: 'left',
                    background: 'transparent',
                    border: 'none',
                    padding: '7px 10px',
                    fontFamily: sansFont,
                    fontSize: 13,
                    color: duration === d ? C.text : C.body,
                    cursor: 'pointer',
                  }}
                >
                  {DURATION_MENU[d]}
                </button>
              ))}
            </div>
          ) : null}
        </div>
        <TextLink onClick={triggerTrust} C={C}>Trust this address</TextLink>
        {props.showIgnore !== false ? (
          <TextLink onClick={triggerIgnore} C={C}>Ignore</TextLink>
        ) : null}
      </div>

      {confirm !== null ? (
        <ConfirmRow
          confirm={confirm}
          target={props.targetDisplay}
          domain={props.siteDomain}
          onConfirm={(pick24h?: boolean) => confirmThen(confirm, pick24h)}
          onCancel={() => setConfirm(null)}
        />
      ) : null}

      {props.error !== undefined && props.error !== null ? (
        <div
          data-testid="block-control-error"
          style={{ fontFamily: sansFont, fontSize: 12, color: C.coralFg, marginTop: 8 }}
        >
          {props.error}
        </div>
      ) : null}
    </div>
  );
}

/**
 * Status line immediately below the BlockControl bar. The sentence
 * renders in sans 12 so it reads as prose (not a kicker). The action
 * link ("Turn on blocking" / "Update the Worker" / "Set up the
 * Worker") is rendered ONLY when the caller passes an `onTurnOn`
 * callback — the caller is the one who knows whether the acting user
 * may mutate the firewall mode (owner / admin). Reviewer / viewer get
 * the sentence without the link.
 */
export function BlockStatusLine({
  blocking,
  siteDomain,
  liveBlocksCount,
  onTurnOn,
  setupHref,
  shopify,
}: {
  readonly blocking: BlockingState;
  readonly siteDomain: string | null;
  /**
   * Number of live blocks already saved on this site. Lets the "on &
   * delivered" sentence say "No addresses are blocked yet" when the
   * envelope is fresh but still empty — founder bug 2026-10-18
   * (vbout.com). Omit when the caller doesn't know (defaults to a
   * generic "Blocking is on." sentence).
   */
  readonly liveBlocksCount?: number;
  /** When provided AND blocking can be switched on here (a current
   *  Worker, blocking off), renders "Turn on blocking". Omit for
   *  reviewer / viewer. */
  readonly onTurnOn?: (() => void) | undefined;
  /** Founder 2026-10-29: "Set up the Worker" / "Update the Worker" go to
   *  Settings › Install. They never open the turn-on confirm, which can
   *  only work with a current Worker in front of the site. */
  readonly setupHref?: string | undefined;
  /** Shopify store with no Worker: Shopify answers requests itself. */
  readonly shopify?: boolean | undefined;
}): JSX.Element {
  const C = useC();
  const sentence = (() => {
    if (blocking.mode === 'on') {
      if (blocking.delivery === 'live') {
        // Founder bug 2026-10-18: a fresh envelope with ZERO entries
        // used to render as "failing" — now it correctly reads as
        // "live", and the sentence makes the empty case explicit.
        if (liveBlocksCount === 0) return 'Blocking is on. No addresses are blocked yet.';
        return 'Blocking is on. Blocked addresses get “Access denied.”';
      }
      if (blocking.delivery === 'pending') return 'Blocking is on. Your Worker picks up changes within a minute.';
      return "Blocking is on, but Sill can't deliver your block list to the Worker right now. Blocks are saved and apply once delivery recovers.";
    }
    if (blocking.install === 'worker_ready') return "Blocking is off. Sill saves blocks but doesn't stop requests yet.";
    if (blocking.install === 'worker_outdated') return 'Update your Cloudflare Worker to turn on blocking.';
    return blockUnavailableReason(blocking, { shopify: shopify === true }) ?? "Sill isn't in your site's request path, so Sill can't stop requests from here.";
  })();
  // Founder 2026-10-18: ●/○ circles replaced with semantic Shield
  // icons so the colour reads as a status, not as a bullet.
  //   ShieldCheck (lime)  — on + delivery live
  //   ShieldAlert (amber) — on + pending / failing
  //   ShieldOff   (muted) — off / not in path / outdated
  const status_icon = blocking.mode === 'on' && blocking.delivery === 'live' ? (
    <ShieldCheck size={13} color={C.limeFg} data-testid="block-status-icon-live" />
  ) : blocking.mode === 'on' ? (
    <ShieldAlert size={13} color={C.amberFg} data-testid="block-status-icon-pending" />
  ) : (
    <ShieldOff size={13} color={C.muted} data-testid="block-status-icon-off" />
  );
  const action: { readonly label: string; readonly kind: 'turn_on' | 'setup' } | null = (() => {
    if (blocking.mode === 'off' && blocking.install === 'worker_ready') return { label: 'Turn on blocking', kind: 'turn_on' };
    if (blocking.install === 'worker_outdated') return { label: 'Update the Worker', kind: 'setup' };
    if (blocking.install === 'not_in_path') {
      // No Worker for Shopify stores until the Shopify-safe Worker ships.
      if (shopify === true && !SHOPIFY_SAFE_WORKER_AVAILABLE) return null;
      return { label: 'Set up the Worker', kind: 'setup' };
    }
    return null;
  })();
  const link_style = {
    background: 'transparent',
    border: 'none',
    padding: 0,
    fontFamily: sansFont,
    fontSize: 12,
    color: C.limeFg,
    cursor: 'pointer',
    textDecoration: 'underline',
  } as const;
  void siteDomain;
  return (
    <div
      data-testid="block-status-line"
      data-blocking-mode={blocking.mode}
      data-delivery={blocking.delivery ?? 'none'}
      data-install={blocking.install}
      style={{
        fontFamily: sansFont,
        fontSize: 12,
        color: C.muted,
        marginTop: 8,
        display: 'flex',
        alignItems: 'flex-start',
        gap: 8,
      }}
    >
      {/* Icon on the first line of the sentence, not on a line of its own. */}
      <span style={{ display: 'inline-flex', height: 18, alignItems: 'center', flexShrink: 0 }}>{status_icon}</span>
      <div style={{ minWidth: 0, lineHeight: '18px' }}>
        <span style={{ color: C.body }}>{sentence}</span>
        {blocking.delivery_warning != null && blocking.delivery_warning.kind === 'install_outdated_truncation' ? (
          <span
            data-testid="block-delivery-warning"
            style={{ color: C.amberFg, marginLeft: 4 }}
          >
            {`Your Worker is out of date; only ${blocking.delivery_warning.published_count} of ${blocking.delivery_warning.total_count} blocks are active. Update to ${blocking.delivery_warning.min_v2_install_version}.`}
          </span>
        ) : null}
        {action !== null && action.kind === 'turn_on' && onTurnOn !== undefined ? (
          <div style={{ marginTop: 6 }}>
            <button type="button" onClick={onTurnOn} data-testid="block-status-action" style={link_style}>
              {action.label}
            </button>
          </div>
        ) : null}
        {action !== null && action.kind === 'setup' && setupHref !== undefined ? (
          <div style={{ marginTop: 6 }}>
            <a href={setupHref} data-testid="block-status-action" style={link_style}>
              {action.label}
            </a>
          </div>
        ) : null}
      </div>
    </div>
  );
}

/** True when the site is a Shopify store (detected platform). */
export function useIsShopifySite(siteId: string | null | undefined): boolean {
  const sites = useOptionalSites();
  if (sites === null || siteId === null || siteId === undefined) return false;
  const site = sites.sites.find((x) => x.site_id === siteId) ?? (sites.activeSite?.site_id === siteId ? sites.activeSite : null);
  return site?.detected_platform === 'shopify';
}

/** Shopify store with no Worker in front of it. */
export function shopifyBlockingSentence(): string {
  return SHOPIFY_SAFE_WORKER_AVAILABLE
    ? 'Shopify answers requests itself. Blocking addresses on Shopify needs the Cloudflare Worker in front of your store.'
    : 'Shopify answers requests itself. Blocking addresses on Shopify needs the Cloudflare Worker in front of your store, coming soon.';
}

function TextLink({
  onClick,
  C,
  children,
}: {
  readonly onClick: () => void;
  readonly C: ReturnType<typeof useC>;
  readonly children: ReactNode;
}): JSX.Element {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        background: 'transparent',
        border: 'none',
        padding: 0,
        fontFamily: sansFont,
        fontSize: 13,
        color: C.body,
        cursor: 'pointer',
        textDecoration: 'underline',
      }}
    >
      {children}
    </button>
  );
}

function ConfirmRow({
  confirm,
  target,
  domain,
  onConfirm,
  onCancel,
}: {
  readonly confirm: ConfirmShape;
  readonly target: string;
  readonly domain: string | null;
  readonly onConfirm: (pick24h?: boolean) => void;
  readonly onCancel: () => void;
}): JSX.Element {
  const C = useC();
  const copy = (() => {
    if (confirm.kind === 'self') {
      // Founder bug 2026-10-18: name the domain twice so the merchant
      // sees exactly WHICH site they'd lock themselves out of (per
      // spec). "This site" was ambiguous when the Sill dashboard is a
      // separate domain from the merchant's own site.
      const d = domain ?? 'this site';
      return {
        body: `This is your current address. When blocking is on for ${d}, you won't be able to open ${d} from this network. The Sill dashboard and your other sites keep working.`,
        confirm: 'Block anyway',
        secondary: 'Cancel',
      };
    }
    if (confirm.kind === 'shared') {
      // Founder bug 2026-10-18: name the domain in the shared-network
      // confirm too, so the merchant knows which site the "forever"
      // block covers.
      const d = domain ?? 'this site';
      return {
        body: `${target} is on a home or mobile network and may be shared by other people. Blocking it forever on ${d} will stop everyone on that network from reaching ${d}. Sill recommends 24 hours.`,
        confirm: 'Block forever anyway',
        secondary: 'Block for 24 hours',
      };
    }
    if (confirm.kind === 'trust') {
      return {
        body: `Trust ${target}? Sill won't alert on or block this address.`,
        confirm: 'Trust',
        secondary: 'Cancel',
      };
    }
    return {
      body: `Block ${target} forever? You can unblock any time. After a year Sill keeps only a hashed copy of the address.`,
      confirm: 'Block forever',
      secondary: 'Cancel',
    };
  })();
  return (
    <div
      data-testid="block-control-confirm"
      data-kind={confirm.kind}
      style={{
        marginTop: 10,
        padding: '10px 12px',
        border: `1px solid ${C.amberFg}`,
        borderRadius: 2,
        fontFamily: sansFont,
        fontSize: 13,
        color: C.text,
      }}
    >
      <div style={{ marginBottom: 8, overflowWrap: 'anywhere' }}>{copy.body}</div>
      <div style={{ display: 'flex', gap: 8 }}>
        <button
          type="button"
          onClick={() => onConfirm(false)}
          data-testid="block-confirm-primary"
          style={primaryBtn(C)}
        >
          {copy.confirm}
        </button>
        <button
          type="button"
          onClick={() => (confirm.kind === 'shared' ? onConfirm(true) : onCancel())}
          data-testid="block-confirm-secondary"
          style={secondaryBtn(C)}
        >
          {copy.secondary}
        </button>
      </div>
    </div>
  );
}

function primaryBtn(C: ReturnType<typeof useC>): React.CSSProperties {
  return {
    fontFamily: sansFont,
    fontSize: 13,
    color: C.ink,
    background: C.lime,
    border: `1px solid ${C.lime}`,
    borderRadius: 2,
    padding: '6px 10px',
    cursor: 'pointer',
  };
}

function secondaryBtn(C: ReturnType<typeof useC>): React.CSSProperties {
  return {
    fontFamily: sansFont,
    fontSize: 13,
    color: C.text,
    background: 'transparent',
    border: `1px solid ${C.border}`,
    borderRadius: 2,
    padding: '6px 10px',
    cursor: 'pointer',
  };
}

/**
 * Founder 2026-10-27 (engin8): why Block is unavailable on this install,
 * or null when blocking can work (a Worker is in front of the site, even
 * if outdated or switched off). Shared by the status line and the greyed
 * Block button's tooltip so both say the same thing.
 */
export function blockUnavailableReason(
  blocking: Pick<BlockingState, 'install' | 'install_source'> | null | undefined,
  opts: { readonly shopify?: boolean } = {},
): string | null {
  if (blocking === null || blocking === undefined || blocking.install !== 'not_in_path') return null;
  if (opts.shopify === true) return shopifyBlockingSentence();
  if (blocking.install_source === 'plugin') {
    return "The WordPress plugin can't block addresses. It refuses attack paths (see Attack paths). To block addresses, add the Sill Cloudflare Worker in front of your site.";
  }
  return "Sill isn't in your site's request path, so Sill can't stop requests from here. To block addresses, add the Sill Cloudflare Worker in front of your site.";
}

/**
 * Founder 2026-10-28 (engin8): the greyed, inert Block shown in place of
 * a per-address Block button when `blockUnavailableReason` is non-null.
 * Hover / focus shows the same reason as the status line.
 */
export function BlockUnavailableButton({ reason, testId }: { readonly reason: string; readonly testId: string }): JSX.Element {
  const C = useC();
  return (
    <Tooltip content={reason} side="bottom">
      <span
        data-testid={testId}
        aria-disabled="true"
        tabIndex={0}
        style={{
          fontFamily: sansFont,
          fontSize: 12,
          padding: '4px 10px',
          background: C.surface2,
          color: C.muted,
          border: `1px solid ${C.border}`,
          borderRadius: 2,
          cursor: 'not-allowed',
          whiteSpace: 'nowrap',
        }}
      >
        Block
      </span>
    </Tooltip>
  );
}
